import { useState, type FormEvent } from 'react';
import { Button, InlineMessage, SelectField, TextField, ToggleGroup } from '../../components';
import { useCapabilities } from '../../hooks/use-capabilities';
import type { CreateSearchPayload, DatePreset, Platform } from '../../types/api';

const PLATFORM_LABELS: Record<Platform, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
};

const PLATFORM_OPTIONS: Array<{ value: Platform; label: string }> = (
  Object.keys(PLATFORM_LABELS) as Platform[]
).map((value) => ({ value, label: PLATFORM_LABELS[value] }));

const PRESET_OPTIONS: Array<{ value: DatePreset; label: string }> = [
  { value: 'last_24h', label: 'Últimas 24h' },
  { value: 'last_7d', label: '7 dias' },
  { value: 'last_30d', label: '30 dias' },
  { value: 'today', label: 'Hoje' },
  { value: 'yesterday', label: 'Ontem' },
  { value: 'this_week', label: 'Esta semana' },
  { value: 'this_month', label: 'Este mês' },
  { value: 'custom', label: 'Personalizado' },
];

export interface SearchFormProps {
  onSubmit: (payload: CreateSearchPayload) => void;
  submitting: boolean;
  errorMessage?: string;
}

/**
 * Formulário de nova pesquisa (RF-F06, RF-F07).
 *
 * A validação de negócio mora no servidor (SPEC-004): aqui só evitamos o
 * disparo obviamente vazio, para não gastar uma ida à API por engano.
 */
export function SearchForm({ onSubmit, submitting, errorMessage }: SearchFormProps) {
  // Os avisos abaixo vêm do que o fornecedor declara, nunca de plataforma
  // codificada aqui (SPEC-025 RF-Q03).
  const capabilities = useCapabilities();
  const capFor = (platform: Platform) =>
    capabilities.data?.platforms.find((c) => c.platform === platform);
  const [mode, setMode] = useState<'subject' | 'person'>('subject');
  const [platforms, setPlatforms] = useState<Platform[]>(['instagram']);
  const [terms, setTerms] = useState('');
  const [phrase, setPhrase] = useState('');
  const [personName, setPersonName] = useState('');
  const [personUsername, setPersonUsername] = useState('');
  const [personProfileUrl, setPersonProfileUrl] = useState('');
  const [personLocation, setPersonLocation] = useState('');
  const [preset, setPreset] = useState<DatePreset>('last_7d');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [fromTime, setFromTime] = useState('');
  const [toTime, setToTime] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const isCustom = preset === 'custom';

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    setLocalError(null);

    if (platforms.length === 0) {
      setLocalError('Selecione ao menos uma plataforma.');
      return;
    }

    if (mode === 'subject' && terms.trim() === '' && phrase.trim() === '') {
      setLocalError('Informe ao menos um termo ou uma frase.');
      return;
    }

    if (
      mode === 'person' &&
      personName.trim() === '' &&
      personUsername.trim() === '' &&
      personProfileUrl.trim() === ''
    ) {
      setLocalError('Informe o nome, o username ou o link do perfil.');
      return;
    }

    if (isCustom && (fromDate === '' || toDate === '')) {
      setLocalError('Informe a data inicial e a data final.');
      return;
    }

    // Termos e hashtags saem do mesmo campo: separar em dois deixaria o
    // formulário mais burocrático sem ganho para quem pesquisa.
    const tokens = terms
      .split(/[,\n]/)
      .map((term) => term.trim())
      .filter((term) => term.length > 0);

    onSubmit({
      type: mode,
      platforms,
      keywords: tokens.filter((token) => !token.startsWith('#')),
      hashtags: tokens.filter((token) => token.startsWith('#')),
      phrase: phrase.trim() === '' ? null : phrase.trim(),
      person:
        mode === 'person'
          ? {
              name: personName.trim() === '' ? null : personName.trim(),
              username: personUsername.trim() === '' ? null : personUsername.trim(),
              profileUrl: personProfileUrl.trim() === '' ? null : personProfileUrl.trim(),
              location: personLocation.trim() === '' ? null : personLocation.trim(),
            }
          : null,
      datePreset: preset,
      customRange: isCustom
        ? {
            fromDate,
            toDate,
            fromTime: fromTime === '' ? null : fromTime,
            toTime: toTime === '' ? null : toTime,
          }
        : null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
      <ToggleGroup
        label="O que você procura"
        single
        options={[
          { value: 'subject', label: 'Assunto' },
          { value: 'person', label: 'Pessoa' },
        ]}
        value={[mode]}
        onChange={(next) => setMode((next[0] as 'subject' | 'person') ?? 'subject')}
      />

      {mode === 'subject' ? (
        <>
          <TextField
            label="Termos e hashtags"
            placeholder="eleições, #política"
            hint="Separe por vírgula. Use # para hashtags."
            value={terms}
            onChange={(event) => setTerms(event.target.value)}
          />
          <TextField
            label="Frase exata (opcional)"
            placeholder="reforma tributária"
            hint="Quando preenchida, é ela que vira o termo pesquisado — não as palavras acima."
            value={phrase}
            onChange={(event) => setPhrase(event.target.value)}
          />
          {/* Forma aceita e precedência, antes do envio (RF-Q04, RF-Q10). */}
          {platforms
            .filter((p) => {
              const busca = capFor(p)?.subjectSearch;
              return busca?.supported === true && busca.shape === 'exact_tag';
            })
            .map((p) => (
              <InlineMessage key={p} tone="warning">
                Em {PLATFORM_LABELS[p]}, a busca por assunto lê a página de <strong>uma</strong>{' '}
                hashtag exata: use uma única palavra, sem espaços. Os demais termos não trazem
                resultados — eles só ordenam o que essa hashtag trouxer.
              </InlineMessage>
            ))}
        </>
      ) : (
        <>
          <TextField
            label="Nome"
            placeholder="João da Silva"
            value={personName}
            onChange={(event) => setPersonName(event.target.value)}
          />
          {/*
            O texto diz o limite antes do uso: o local só reordena o que a busca
            por nome trouxe — nenhum fornecedor filtra perfil por lugar
            (SPEC-005 §8.6).
          */}
          <TextField
            label="Local (opcional)"
            placeholder="Campinas"
            hint="Não filtra nem traz perfis novos: favorece os perfis encontrados cuja biografia cita este local."
            value={personLocation}
            onChange={(event) => setPersonLocation(event.target.value)}
          />
          <TextField
            label="Username (opcional)"
            placeholder="@joao.silva"
            value={personUsername}
            onChange={(event) => setPersonUsername(event.target.value)}
          />
          <TextField
            label="Link do perfil ou da página (opcional)"
            placeholder="https://www.facebook.com/prefeitura"
            hint="Com o link, a coleta vai direto ao perfil — sem passar pela escolha de perfis."
            value={personProfileUrl}
            onChange={(event) => setPersonProfileUrl(event.target.value)}
          />
          {/*
            A assimetria é declarada antes da tentativa, não descoberta por um
            resultado vazio (SPEC-014 §17). Vem do fornecedor, e por isso é lida
            das capabilities — codificar "Facebook é assim" aqui faria o
            frontend conhecer fornecedor (SPEC-025 RF-Q03).
          */}
          {platforms
            .filter((p) => capFor(p)?.personSearch.requiresProfileUrl === true)
            .map((p) => (
              <InlineMessage key={p} tone="warning">
                Em {PLATFORM_LABELS[p]} não existe busca por nome: a coleta depende do link do
                perfil ou da página. Sem o link acima, esta plataforma não trará resultados.
              </InlineMessage>
            ))}
        </>
      )}

      <ToggleGroup
        label="Plataformas"
        options={PLATFORM_OPTIONS}
        value={platforms}
        onChange={setPlatforms}
      />

      <SelectField
        label="Período"
        value={preset}
        onChange={(event) => setPreset(event.target.value as DatePreset)}
        options={PRESET_OPTIONS}
      />

      {isCustom && (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Data inicial"
            type="date"
            value={fromDate}
            onChange={(event) => setFromDate(event.target.value)}
          />
          <TextField
            label="Data final"
            type="date"
            value={toDate}
            onChange={(event) => setToDate(event.target.value)}
          />
          <TextField
            label="Hora inicial (opcional)"
            type="time"
            value={fromTime}
            onChange={(event) => setFromTime(event.target.value)}
          />
          <TextField
            label="Hora final (opcional)"
            type="time"
            value={toTime}
            onChange={(event) => setToTime(event.target.value)}
          />
        </div>
      )}

      {(localError ?? errorMessage) && (
        <InlineMessage tone="danger">{localError ?? errorMessage}</InlineMessage>
      )}

      <div>
        <Button type="submit" loading={submitting}>
          Pesquisar
        </Button>
      </div>
    </form>
  );
}
